import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { milestoneApi } from '../services/api';
import { apiErrorText, saveErrorText } from '../utils/apiError';
import type {
  Milestone,
  MilestoneBankIdSessionResponse
} from '../types';

interface DisputeCooldownPanelProps {
  milestone: Milestone;
  onStartConflict: () => Promise<void>;
  onStartDispute?: (verificationRef: string) => Promise<void>;
  onDownloadExport?: () => Promise<void>;
  onOpenDocuments?: () => void;
  canStartDispute?: boolean;
  viewerSide?: 'person' | 'company';
  onOpenMarketplace?: () => void;
}

export const CONFLICT_CONFIRMATION_PHRASE = 'ja jag vill göra det';

export default function DisputeCooldownPanel({
  milestone,
  onStartConflict,
  onStartDispute,
  onDownloadExport,
  onOpenDocuments,
  canStartDispute = false,
  viewerSide,
  onOpenMarketplace
}: DisputeCooldownPanelProps) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [conflictConfirmationPhrase, setConflictConfirmationPhrase] = useState('');
  const [showConflictGuide, setShowConflictGuide] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [disputeConfirmationPhrase, setDisputeConfirmationPhrase] = useState('');
  const [disputeStatus, setDisputeStatus] = useState<'idle' | 'starting' | 'waiting' | 'saving' | 'success' | 'cancelled' | 'failed'>('idle');
  const [bankIdSession, setBankIdSession] = useState<MilestoneBankIdSessionResponse | null>(null);
  const [bankIdQrImage, setBankIdQrImage] = useState<string | null>(null);
  const [disputeError, setDisputeError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conflictError, setConflictError] = useState('');
  const disputeRunRef = useRef(0);
  const isDisputed = milestone.deliveryStatus === 'disputed';
  const isUnderResolution = milestone.deliveryStatus === 'under_resolution';
  const isOngoingWork = ['in_progress', 'ready_for_attestation', 'seller_attested'].includes(milestone.deliveryStatus ?? '') &&
    !(milestone.companyAttestation === 'approved' && milestone.personAttestation === 'approved');
  const hasActiveConflict = Boolean(milestone.conflictStartedAt);
  const canStartConflict = (isOngoingWork || isDisputed || isUnderResolution) && !hasActiveConflict;
  const isConflict = milestone.deliveryStatus === 'conflict' || hasActiveConflict;
  const isExternalDispute = milestone.deliveryStatus === 'external_dispute';
  useEffect(() => () => {
    ++disputeRunRef.current;
  }, []);

  useEffect(() => {
    const qrData = bankIdSession?.qrCode?.data;
    if (!qrData) {
      setBankIdQrImage(null);
      return;
    }
    let active = true;
    QRCode.toDataURL(qrData, { width: 192, margin: 1 })
      .then(image => {
        if (active) setBankIdQrImage(image);
      })
      .catch(() => {
        if (active) setBankIdQrImage(null);
      });
    return () => {
      active = false;
    };
  }, [bankIdSession?.qrCode?.data]);

  if (!canStartConflict && !isConflict && !isExternalDispute) return null;

  const startConflict = async () => {
    setIsSubmitting(true);
    setConflictError('');
    try {
      await onStartConflict();
      setConfirming(false);
      setConflictConfirmationPhrase('');
      setShowConflictGuide(true);
    } catch (error) {
      // Shown in the dialog, which stays open for another try. If the conflict
      // started anyway, the refreshed stage closes the dialog (ClickUp 869fgxq45).
      setConflictError(saveErrorText(t, error, 'disputeCooldown.startConflictFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const wait = (milliseconds: number) =>
    new Promise(resolve => window.setTimeout(resolve, milliseconds));

  const handleDisputeSession = async (
    session: MilestoneBankIdSessionResponse,
    runId: number
  ) => {
    if (disputeRunRef.current !== runId) return true;
    setBankIdSession(session);
    if (session.status === 'complete' || session.verified) {
      setDisputeStatus('saving');
      try {
        await onStartDispute?.(session.verificationRef);
        if (disputeRunRef.current === runId) setDisputeStatus('success');
      } catch (error) {
        if (disputeRunRef.current === runId) {
          setDisputeError(apiErrorText(t, error, 'disputeCooldown.disputeStartFailedAfterBankId'));
          setDisputeStatus('failed');
        }
      }
      return true;
    }
    if (session.status === 'cancelled') {
      setDisputeStatus('cancelled');
      return true;
    }
    if (session.status === 'failed' || session.status === 'expired') {
      setDisputeError(session.message || t('disputeCooldown.bankIdVerificationFailed'));
      setDisputeStatus('failed');
      return true;
    }
    return false;
  };

  const startDispute = async () => {
    const runId = ++disputeRunRef.current;
    setDisputeStatus('starting');
    setDisputeError(null);
    setBankIdSession(null);
    try {
      let session = await milestoneApi.startDisputeBankId(milestone.id);
      if (disputeRunRef.current !== runId) return;
      setBankIdSession(session);
      setDisputeStatus('waiting');
      if (await handleDisputeSession(session, runId)) return;
      while (disputeRunRef.current === runId) {
        await wait(session.pollIntervalMs ?? 1500);
        if (disputeRunRef.current !== runId) return;
        session = session.provider === 'simulated'
          ? await milestoneApi.collectDisputeBankId(milestone.id, session.verificationRef)
          : await milestoneApi.getDisputeBankIdStatus(milestone.id, session.verificationRef);
        if (await handleDisputeSession(session, runId)) return;
      }
    } catch {
      if (disputeRunRef.current !== runId) return;
      setDisputeError(t('disputeCooldown.bankIdVerificationCouldNotComplete'));
      setDisputeStatus('failed');
    }
  };

  const cancelDisputeBankId = async () => {
    ++disputeRunRef.current;
    if (bankIdSession?.verificationRef) {
      try {
        await milestoneApi.cancelDisputeBankId(
          milestone.id,
          bankIdSession.verificationRef
        );
      } catch {
        // Den lokala dialogen stängs även om leverantörens session löper ut senare.
      }
    }
    setDisputeStatus('cancelled');
  };

  if (isExternalDispute) {
    return (
      <div className="alert alert-danger mt-3 mb-0">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div>
            <div className="fw-semibold">
              <i className="feather-x-octagon me-2"></i>
              {t('disputeCooldown.externalDisputeTitle')}
            </div>
            <div className="small mt-1">
              {t('disputeCooldown.externalDisputeBody')}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 flex-shrink-0">
            {onOpenDocuments && (
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={onOpenDocuments}
              >
                <i className="feather-folder me-1"></i>
                {t('disputeCooldown.showProjectDocuments')}
              </button>
            )}
            {onDownloadExport && (
              <button
                className="btn btn-light btn-sm"
                onClick={() => void onDownloadExport()}
              >
                <i className="feather-download me-1"></i>
                {t('disputeCooldown.downloadDisputeExport')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="alert alert-warning mt-3 mb-0">
        <div className="d-flex align-items-start gap-2">
          <i className="feather-alert-triangle mt-1"></i>
          <div className="flex-grow-1">
            <div className="fw-semibold">
              {isOngoingWork
                ? t('disputeCooldown.ongoingWorkTitle')
                : isUnderResolution
                ? t('disputeCooldown.remediationInProgress')
                : isDisputed
                  ? t('disputeCooldown.giveDialogTime')
                  : t('disputeCooldown.giveConflictResolutionTime')}
            </div>
            <div className="small">
              {isOngoingWork
                ? t('disputeCooldown.ongoingWorkBody')
                : isUnderResolution
                ? t('disputeCooldown.remediationCooldownBody')
                : isDisputed
                  ? t('disputeCooldown.disputedCooldownBody')
                  : t('disputeCooldown.conflictCooldownBody')}
            </div>
          </div>
        </div>
        {canStartConflict ? (
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
            <button
              className="btn btn-outline-danger btn-sm"
              disabled={isSubmitting}
              onClick={() => setConfirming(true)}
            >
              <i className="feather-alert-triangle me-1" aria-hidden="true"></i>
              {t('disputeCooldown.startConflictMode')}
            </button>
            <span className="small text-muted">
              {t('disputeCooldown.inviteLawyerInspector')}
            </span>
          </div>
        ) : (
          <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowConflictGuide(true)}
            >
              <i className="feather-users me-1"></i>
              {t('disputeCooldown.getHelpInConflict')}
            </button>
            {canStartDispute && onStartDispute && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => {
                  setDisputeStatus('idle');
                  setDisputeConfirmationPhrase('');
                  setShowDispute(true);
                }}
              >
                <i className="feather-x-octagon me-1"></i>
                {t('disputeCooldown.startDispute')}
              </button>
            )}
            <span className="small text-muted">
              {t('disputeCooldown.nextEscalationIsDispute')}
            </span>
          </div>
        )}
      </div>

      {canStartConflict && confirming && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="start-conflict-title"
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="start-conflict-title">{t('disputeCooldown.confirmConflictModeTitle')}</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setConfirming(false);
                    setConflictConfirmationPhrase('');
                  }}
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-body">
                <p>
                  {t(isOngoingWork ? 'disputeCooldown.confirmOngoingConflictBody' : 'disputeCooldown.confirmConflictBody')}
                </p>
                <div className="alert alert-info mb-0 small">
                  {t('disputeCooldown.conflictModeCanInvite')}
                </div>
                <label className="form-label fw-semibold mt-3" htmlFor="confirm-conflict-phrase">
                  {t('disputeCooldown.typeConfirmationPhrase', {
                    phrase: CONFLICT_CONFIRMATION_PHRASE
                  })}
                </label>
                <input
                  id="confirm-conflict-phrase"
                  className="form-control"
                  value={conflictConfirmationPhrase}
                  disabled={isSubmitting}
                  autoComplete="off"
                  onChange={event => setConflictConfirmationPhrase(event.target.value)}
                />
                {conflictError && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    {conflictError}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setConfirming(false);
                    setConflictConfirmationPhrase('');
                  }}
                  disabled={isSubmitting}
                >
                  {t('disputeCooldown.cancel')}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={startConflict}
                  disabled={
                    isSubmitting ||
                    conflictConfirmationPhrase !== CONFLICT_CONFIRMATION_PHRASE
                  }
                >
                  <i className="feather-alert-triangle me-1" aria-hidden="true"></i>
                  {isSubmitting ? t('disputeCooldown.startingConflict') : t('disputeCooldown.startConflictMode')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConflictGuide && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="conflict-guide-title"
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="conflict-guide-title">
                  <i className="feather-alert-triangle text-warning me-2"></i>
                  {t('disputeCooldown.conflictModeStartedTitle')}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowConflictGuide(false)}
                  aria-label={t('disputeCooldown.close')}
                />
              </div>
              <div className="modal-body">
                <p>
                  {t('disputeCooldown.conflictGuideIntro')}
                </p>
                <div className="border rounded p-3 mb-3">
                  <div className="fw-semibold mb-1">
                    <i className="feather-message-circle text-primary me-2"></i>
                    {t('disputeCooldown.keepTryingToAgree')}
                  </div>
                  <div className="small text-muted">
                    {t('disputeCooldown.keepTryingToAgreeBody')}
                  </div>
                </div>
                <div className="border rounded p-3">
                  <div className="fw-semibold mb-1">
                    <i className="feather-users text-primary me-2"></i>
                    {t('disputeCooldown.getHelpIfNeeded')}
                  </div>
                  <div className="small text-muted">
                    {t('disputeCooldown.getHelpIfNeededBody')}
                  </div>
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowConflictGuide(false)}
                >
                  {t('disputeCooldown.continueDialog')}
                </button>
                {onOpenMarketplace && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowConflictGuide(false);
                      onOpenMarketplace();
                    }}
                  >
                    <i className="feather-shopping-bag me-1"></i>
                    {t('disputeCooldown.findHelpOnMarketplace')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDispute && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1080 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="start-dispute-title"
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="start-dispute-title">
                  <i className="feather-x-octagon text-danger me-2"></i>
                  {t('disputeCooldown.leaveEtappioStartDispute')}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowDispute(false);
                    setDisputeConfirmationPhrase('');
                  }}
                  disabled={['starting', 'waiting', 'saving'].includes(disputeStatus)}
                  aria-label={t('disputeCooldown.close')}
                />
              </div>
              <div className="modal-body">
                {disputeStatus === 'idle' && (
                  <>
                    <p>
                      {t('disputeCooldown.disputeIdleBody')}
                    </p>
                    <div className="alert alert-warning small">
                      {viewerSide === 'person'
                        ? t('disputeCooldown.disputeIdleFeesWarningNoFee')
                        : t('disputeCooldown.disputeIdleFeesWarning')}
                    </div>
                    <div className="small text-muted">
                      {t('disputeCooldown.disputeIdleExportNote')}
                    </div>
                    <label className="form-label fw-semibold mt-3" htmlFor="confirm-dispute-phrase">
                      {t('disputeCooldown.typeConfirmationPhrase', {
                        phrase: CONFLICT_CONFIRMATION_PHRASE
                      })}
                    </label>
                    <input
                      id="confirm-dispute-phrase"
                      className="form-control"
                      value={disputeConfirmationPhrase}
                      autoComplete="off"
                      onChange={event => setDisputeConfirmationPhrase(event.target.value)}
                    />
                  </>
                )}
                {disputeStatus === 'starting' && (
                  <div className="text-center py-4" role="status">
                    <div className="spinner-border text-primary mb-3"></div>
                    <h6>{t('disputeCooldown.startingBankId')}</h6>
                  </div>
                )}
                {disputeStatus === 'waiting' && (
                  <div className="text-center py-2" role="status">
                    {bankIdQrImage ? (
                      <div className="d-inline-flex bg-white border rounded p-2 mb-3">
                        <img src={bankIdQrImage} alt={t('disputeCooldown.bankIdQrAlt')} width="192" height="192" />
                      </div>
                    ) : (
                      <div className="spinner-border text-primary mb-3"></div>
                    )}
                    <h6>{t('disputeCooldown.waitingForBankId')}</h6>
                    <p className="text-muted">
                      {bankIdSession?.message || t('disputeCooldown.openBankIdApp')}
                    </p>
                    {bankIdSession?.sameDeviceUrl && (
                      <a className="btn btn-primary btn-sm" href={bankIdSession.sameDeviceUrl}>
                        {t('disputeCooldown.openBankId')}
                      </a>
                    )}
                  </div>
                )}
                {disputeStatus === 'saving' && (
                  <div className="text-center py-4" role="status">
                    <div className="spinner-border text-danger mb-3"></div>
                    <h6>{t('disputeCooldown.startingDisputeArrangingRefund')}</h6>
                  </div>
                )}
                {disputeStatus === 'success' && (
                  <div className="text-center py-4" role="status">
                    <i className="feather-x-octagon text-danger d-block mb-3" style={{ fontSize: '4rem' }}></i>
                    <h6 className="text-danger">{t('disputeCooldown.disputeStartedTitle')}</h6>
                    <p className="text-muted mb-0">
                      {t('disputeCooldown.disputeStartedBody')}
                    </p>
                  </div>
                )}
                {disputeStatus === 'cancelled' && (
                  <div className="text-center py-4">
                    <i className="feather-x-circle text-warning d-block mb-3" style={{ fontSize: '3rem' }}></i>
                    <h6>{t('disputeCooldown.disputeNotStarted')}</h6>
                  </div>
                )}
                {disputeStatus === 'failed' && (
                  <div className="text-center py-4" role="alert">
                    <i className="feather-alert-circle text-danger d-block mb-3" style={{ fontSize: '3rem' }}></i>
                    <h6 className="text-danger">{t('disputeCooldown.disputeCouldNotStart')}</h6>
                    <p className="text-muted mb-0">{disputeError}</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {disputeStatus === 'idle' && (
                  <>
                    <button
                      className="btn btn-light"
                      onClick={() => {
                        setShowDispute(false);
                        setDisputeConfirmationPhrase('');
                      }}
                    >
                      {t('disputeCooldown.cancel')}
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => void startDispute()}
                      disabled={disputeConfirmationPhrase !== CONFLICT_CONFIRMATION_PHRASE}
                    >
                      <i className="feather-shield me-1"></i>
                      {t('disputeCooldown.confirmWithBankId')}
                    </button>
                  </>
                )}
                {['starting', 'waiting'].includes(disputeStatus) && (
                  <button className="btn btn-outline-danger ms-auto" onClick={() => void cancelDisputeBankId()}>
                    {t('disputeCooldown.cancelBankId')}
                  </button>
                )}
                {disputeStatus === 'failed' && (
                  <button className="btn btn-danger ms-auto" onClick={() => void startDispute()}>{t('disputeCooldown.tryAgain')}</button>
                )}
                {['success', 'cancelled'].includes(disputeStatus) && (
                  <button className="btn btn-primary ms-auto" onClick={() => setShowDispute(false)}>{t('disputeCooldown.close')}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
